import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import './AdminDashboard.css';

/* ═══════════════════════════════
   MOCK DATA
   ═══════════════════════════════ */

const trafficData = [
  { name: 'Jan', views: 22000, subs: 8500 },
  { name: 'Feb', views: 35000, subs: 12000 },
  { name: 'Mar', views: 48000, subs: 18000 },
  { name: 'Apr', views: 62000, subs: 24000 },
  { name: 'May', views: 58000, subs: 28000 },
  { name: 'Jun', views: 75000, subs: 32000 },
  { name: 'Jul', views: 88000, subs: 38000 },
  { name: 'Aug', views: 95000, subs: 42000 },
  { name: 'Sep', views: 82000, subs: 45000 },
  { name: 'Oct', views: 110000, subs: 52000 },
  { name: 'Nov', views: 125000, subs: 58000 },
  { name: 'Dec', views: 148000, subs: 64000 },
];

const planDistribution = [
  { name: 'Basic', value: 4200, color: '#6B7280' },
  { name: 'Gold', value: 2800, color: '#F59E0B' },
  { name: 'Diamond', value: 1500, color: '#06B6D4' },
  { name: 'Platinum', value: 850, color: '#8B5CF6' },
];

const revenueByPlan = [
  { name: 'Basic', revenue: 84000, subscribers: 4200 },
  { name: 'Gold', revenue: 196000, subscribers: 2800 },
  { name: 'Diamond', revenue: 225000, subscribers: 1500 },
  { name: 'Platinum', revenue: 212500, subscribers: 850 },
];

const recentUsers = [
  { id: 1, email: 'priya.sharma@gmail.com', plan: 'Platinum', date: '2026-04-09', status: 'active' },
  { id: 2, email: 'rahul.verma@outlook.com', plan: 'Gold', date: '2026-04-09', status: 'active' },
  { id: 3, email: 'deepak.patel@yahoo.com', plan: 'Diamond', date: '2026-04-08', status: 'active' },
  { id: 4, email: 'sneha.gupta@gmail.com', plan: 'Basic', date: '2026-04-08', status: 'active' },
  { id: 5, email: 'amit.kumar@gmail.com', plan: 'Gold', date: '2026-04-07', status: 'inactive' },
  { id: 6, email: 'ananya.reddy@outlook.com', plan: 'Platinum', date: '2026-04-07', status: 'active' },
  { id: 7, email: 'vikram.singh@gmail.com', plan: 'Diamond', date: '2026-04-06', status: 'active' },
  { id: 8, email: 'neha.jain@yahoo.com', plan: 'Basic', date: '2026-04-06', status: 'suspended' },
  { id: 9, email: 'kartik.mishra@gmail.com', plan: 'Gold', date: '2026-04-05', status: 'active' },
  { id: 10, email: 'pooja.nair@outlook.com', plan: 'Basic', date: '2026-04-05', status: 'active' },
];

const allUsers = [
  ...recentUsers,
  { id: 11, email: 'suresh.iyer@gmail.com', plan: 'Gold', date: '2026-04-04', status: 'active' },
  { id: 12, email: 'kaviya.menon@yahoo.com', plan: 'Basic', date: '2026-04-04', status: 'active' },
  { id: 13, email: 'rajan.choudhury@gmail.com', plan: 'Diamond', date: '2026-04-03', status: 'inactive' },
  { id: 14, email: 'meera.das@outlook.com', plan: 'Platinum', date: '2026-04-03', status: 'active' },
  { id: 15, email: 'arjun.pillai@gmail.com', plan: 'Gold', date: '2026-04-02', status: 'active' },
  { id: 16, email: 'divya.saxena@yahoo.com', plan: 'Basic', date: '2026-04-02', status: 'suspended' },
  { id: 17, email: 'rohit.agarwal@gmail.com', plan: 'Diamond', date: '2026-04-01', status: 'active' },
  { id: 18, email: 'shreya.bose@outlook.com', plan: 'Gold', date: '2026-04-01', status: 'active' },
  { id: 19, email: 'manoj.tiwari@gmail.com', plan: 'Basic', date: '2026-03-31', status: 'active' },
  { id: 20, email: 'rashmi.joshi@yahoo.com', plan: 'Platinum', date: '2026-03-31', status: 'active' },
];

const expiringUsers = [
  { email: 'amit.kumar@gmail.com', plan: 'Gold', expiresIn: '2 days' },
  { email: 'neha.jain@yahoo.com', plan: 'Basic', expiresIn: '3 days' },
  { email: 'rajan.choudhury@gmail.com', plan: 'Diamond', expiresIn: '5 days' },
  { email: 'divya.saxena@yahoo.com', plan: 'Basic', expiresIn: '7 days' },
];

const deviceData = [
  { name: 'Mobile', value: 62, color: '#8B5CF6' },
  { name: 'Desktop', value: 28, color: '#06B6D4' },
  { name: 'Tablet', value: 10, color: '#F59E0B' },
];

const cityData = [
  { city: 'Mumbai', users: 2840, pct: 30 },
  { city: 'Delhi', users: 1890, pct: 20 },
  { city: 'Bengaluru', users: 1510, pct: 16 },
  { city: 'Hyderabad', users: 950, pct: 10 },
  { city: 'Chennai', users: 760, pct: 8 },
  { city: 'Pune', users: 570, pct: 6 },
  { city: 'Others', users: 830, pct: 10 },
];

const featureUsage = [
  { name: 'Sector Rotation', icon: '📊', count: '8.2K' },
  { name: 'Intraday Scan', icon: '📈', count: '7.5K' },
  { name: 'Momentum Radar', icon: '📡', count: '6.1K' },
  { name: 'Heatmap', icon: '🔥', count: '5.8K' },
  { name: 'OI Monitor', icon: '📊', count: '4.3K' },
  { name: 'Swing Zone', icon: '🎯', count: '3.9K' },
  { name: 'Traders Diary', icon: '📖', count: '2.7K' },
  { name: 'SIP Calculator', icon: '📱', count: '2.1K' },
];

const peakHours = [
  { hour: '9 AM', usage: 85 }, { hour: '10 AM', usage: 95 },
  { hour: '11 AM', usage: 78 }, { hour: '12 PM', usage: 55 },
  { hour: '1 PM', usage: 42 }, { hour: '2 PM', usage: 68 },
  { hour: '3 PM', usage: 92 }, { hour: '4 PM', usage: 45 },
];

/* Sparkline data for stat cards */
const sparklines = {
  users: [4, 6, 5, 8, 7, 9, 8, 10, 9, 12],
  subs: [3, 5, 4, 7, 6, 8, 9, 8, 10, 9],
  revenue: [5, 4, 7, 6, 8, 7, 9, 11, 10, 12],
  views: [6, 8, 7, 9, 8, 10, 9, 11, 13, 15],
};

/* ═══════════════════════════════
   COMPONENT
   ═══════════════════════════════ */

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 8;

  // Check admin auth
  useEffect(() => {
    const isAdmin = sessionStorage.getItem('adminAuthenticated');
    if (!isAdmin) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const handleAdminLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    if (onLogout) onLogout();
    navigate('/');
  };

  /* ── Navigation items ── */
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'blog', label: 'Blog Manager', icon: '✍️' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  /* ── Filtered users for Users tab ── */
  const filteredUsers = allUsers.filter(u =>
    u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.plan.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.status.toLowerCase().includes(userSearch.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  /* ── Custom tooltip ── */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(15, 15, 40, 0.95)',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '10px',
          padding: '10px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '6px' }}>{label}</p>
          {payload.map((entry, i) => (
            <p key={i} style={{ color: entry.color, fontSize: '0.82rem', fontWeight: 600, margin: '2px 0' }}>
              {entry.name}: {(entry.value / 1000).toFixed(1)}K
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  /* ── Render sparkline ── */
  const renderSparkline = (data, color) => {
    const max = Math.max(...data);
    return (
      <div className="adm-stat-sparkline">
        {data.map((val, i) => (
          <div
            key={i}
            className="adm-spark-bar"
            style={{
              height: `${(val / max) * 100}%`,
              background: `${color}40`,
            }}
          />
        ))}
      </div>
    );
  };

  /* ════════════════════════════
     TAB: DASHBOARD
     ════════════════════════════ */
  const renderDashboard = () => (
    <>
      {/* KPI Stats */}
      <div className="adm-stats-grid">
        <div className="adm-stat-card">
          <div className="adm-stat-accent purple" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Total Users</span>
            <div className="adm-stat-icon-wrap purple">👥</div>
          </div>
          <div className="adm-stat-value">9,350</div>
          <span className="adm-stat-change up">↑ 12.4% this month</span>
          {renderSparkline(sparklines.users, '#8B5CF6')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent cyan" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Active Subscriptions</span>
            <div className="adm-stat-icon-wrap cyan">💳</div>
          </div>
          <div className="adm-stat-value">5,150</div>
          <span className="adm-stat-change up">↑ 8.7% this month</span>
          {renderSparkline(sparklines.subs, '#06B6D4')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent emerald" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Monthly Revenue</span>
            <div className="adm-stat-icon-wrap emerald">💰</div>
          </div>
          <div className="adm-stat-value">₹7.17L</div>
          <span className="adm-stat-change up">↑ 15.2% this month</span>
          {renderSparkline(sparklines.revenue, '#10B981')}
        </div>

        <div className="adm-stat-card">
          <div className="adm-stat-accent amber" />
          <div className="adm-stat-header">
            <span className="adm-stat-label">Page Views</span>
            <div className="adm-stat-icon-wrap amber">👁️</div>
          </div>
          <div className="adm-stat-value">148K</div>
          <span className="adm-stat-change up">↑ 18.4% this month</span>
          {renderSparkline(sparklines.views, '#F59E0B')}
        </div>
      </div>

      {/* Charts row */}
      <div className="adm-charts-row">
        {/* Area chart */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <span className="adm-chart-title">Traffic & Subscriptions</span>
            <div className="adm-chart-legend">
              <span className="adm-legend-item"><span className="adm-legend-dot pink" /> Page Views</span>
              <span className="adm-legend-item"><span className="adm-legend-dot cyan" /> Subscriptions</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trafficData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="admGradViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D946EF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D946EF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="admGradSubs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="name" stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} />
              <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} tickFormatter={(v) => `${v / 1000}K`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="views" name="Page Views" stroke="#D946EF" strokeWidth={2} fill="url(#admGradViews)" />
              <Area type="monotone" dataKey="subs" name="Subscriptions" stroke="#06B6D4" strokeWidth={2} fill="url(#admGradSubs)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Plan distribution donut */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <span className="adm-chart-title">Plan Distribution</span>
          </div>
          <div className="adm-donut-container">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {planDistribution.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,15,40,0.95)',
                    border: '1px solid rgba(139,92,246,0.2)',
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                    color: '#fff',
                  }}
                  formatter={(value) => [`${value} users`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="adm-donut-legend">
              {planDistribution.map((item, i) => (
                <div key={i} className="adm-donut-legend-item">
                  <div className="adm-donut-legend-left">
                    <span className="adm-donut-legend-dot" style={{ background: item.color }} />
                    <span className="adm-donut-legend-name">{item.name}</span>
                  </div>
                  <span className="adm-donut-legend-value">{item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="adm-quick-actions">
        <button className="adm-quick-action" onClick={() => navigate('/admin/blog')}>
          <div className="adm-quick-action-icon">✍️</div>
          <div className="adm-quick-action-text">
            <h4>Blog Admin</h4>
            <p>Manage posts & articles</p>
          </div>
        </button>
        <button className="adm-quick-action" onClick={() => alert('Data exported successfully!')}>
          <div className="adm-quick-action-icon">📥</div>
          <div className="adm-quick-action-text">
            <h4>Export Data</h4>
            <p>Download analytics CSV</p>
          </div>
        </button>
        <button className="adm-quick-action" onClick={() => alert('Push notification sent!')}>
          <div className="adm-quick-action-icon">🔔</div>
          <div className="adm-quick-action-text">
            <h4>Send Notification</h4>
            <p>Push to all users</p>
          </div>
        </button>
      </div>

      {/* Recent signups table */}
      <div className="adm-table-card">
        <div className="adm-table-header">
          <span className="adm-table-title">Recent Signups</span>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td><span className={`adm-plan-badge ${user.plan.toLowerCase()}`}>{user.plan}</span></td>
                  <td>{user.date}</td>
                  <td><span className={`adm-badge ${user.status}`}>{user.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: USERS
     ════════════════════════════ */
  const renderUsers = () => (
    <>
      <div className="adm-table-card">
        <div className="adm-table-header">
          <span className="adm-table-title">All Users ({filteredUsers.length})</span>
          <div className="adm-table-actions">
            <input
              type="text"
              className="adm-search-input"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => { setUserSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Joined</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td><span className={`adm-plan-badge ${user.plan.toLowerCase()}`}>{user.plan}</span></td>
                  <td>{user.date}</td>
                  <td><span className={`adm-badge ${user.status}`}>{user.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="adm-pagination">
            <button className="adm-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>←</button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`adm-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button className="adm-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>→</button>
          </div>
        )}
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: SUBSCRIPTIONS
     ════════════════════════════ */
  const renderSubscriptions = () => (
    <>
      {/* Plan cards */}
      <div className="adm-sub-cards">
        {planDistribution.map((plan, i) => (
          <div key={i} className="adm-sub-card" style={{ borderTop: `3px solid ${plan.color}` }}>
            <div className="adm-sub-plan-name">{plan.name}</div>
            <div className="adm-sub-count">{plan.value.toLocaleString()}</div>
            <div className="adm-sub-revenue">₹{(revenueByPlan[i].revenue / 1000).toFixed(0)}K revenue</div>
          </div>
        ))}
      </div>

      {/* Revenue by plan chart */}
      <div className="adm-chart-card" style={{ marginBottom: '1.75rem' }}>
        <div className="adm-chart-header">
          <span className="adm-chart-title">Revenue by Plan</span>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revenueByPlan} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="name" stroke="none" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} tickLine={false} />
            <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} tickFormatter={(v) => `₹${v / 1000}K`} />
            <Tooltip
              contentStyle={{
                background: 'rgba(15,15,40,0.95)',
                border: '1px solid rgba(139,92,246,0.2)',
                borderRadius: '8px',
                color: '#fff',
              }}
              formatter={(value) => [`₹${(value / 1000).toFixed(0)}K`, 'Revenue']}
            />
            <Bar dataKey="revenue" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expiring soon */}
      <div className="adm-expiring-alerts">
        <div className="adm-expiring-title">⚠️ Subscriptions Expiring Soon</div>
        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Plan</th>
                <th>Expires In</th>
              </tr>
            </thead>
            <tbody>
              {expiringUsers.map((u, i) => (
                <tr key={i}>
                  <td>{u.email}</td>
                  <td><span className={`adm-plan-badge ${u.plan.toLowerCase()}`}>{u.plan}</span></td>
                  <td><span className="adm-badge inactive">{u.expiresIn}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: ANALYTICS
     ════════════════════════════ */
  const renderAnalytics = () => (
    <>
      <div className="adm-analytics-grid">
        {/* Device breakdown */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Device Breakdown</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={deviceData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value" stroke="none">
                {deviceData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: 'rgba(15,15,40,0.95)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => [`${value}%`, '']}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="adm-donut-legend">
            {deviceData.map((d, i) => (
              <div key={i} className="adm-donut-legend-item">
                <div className="adm-donut-legend-left">
                  <span className="adm-donut-legend-dot" style={{ background: d.color }} />
                  <span className="adm-donut-legend-name">{d.name}</span>
                </div>
                <span className="adm-donut-legend-value">{d.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic distribution */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Top Cities</div>
          <div className="adm-progress-list">
            {cityData.map((city, i) => (
              <div key={i} className="adm-progress-item">
                <div className="adm-progress-header">
                  <span className="adm-progress-label">{city.city}</span>
                  <span className="adm-progress-value">{city.users.toLocaleString()} ({city.pct}%)</span>
                </div>
                <div className="adm-progress-bar">
                  <div
                    className="adm-progress-fill"
                    style={{
                      width: `${city.pct * 3.3}%`,
                      background: `linear-gradient(90deg, #8B5CF6, #06B6D4)`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Peak usage hours */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Peak Usage Hours</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={peakHours} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="hour" stroke="none" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} />
              <YAxis stroke="none" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'rgba(15,15,40,0.95)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => [`${value}%`, 'Usage']}
              />
              <Bar dataKey="usage" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Feature usage */}
        <div className="adm-analytics-card">
          <div className="adm-analytics-card-title">Feature Usage</div>
          <div className="adm-feature-list">
            {featureUsage.map((f, i) => (
              <div key={i} className="adm-feature-item">
                <div className="adm-feature-left">
                  <span className="adm-feature-icon">{f.icon}</span>
                  <span className="adm-feature-name">{f.name}</span>
                </div>
                <span className="adm-feature-count">{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  /* ════════════════════════════
     TAB: BLOG MANAGER
     ════════════════════════════ */
  const renderBlogManager = () => (
    <>
      <div className="adm-blog-overview">
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">📝</div>
          <div className="adm-blog-stat-value">24</div>
          <div className="adm-blog-stat-label">Total Posts</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">👁️</div>
          <div className="adm-blog-stat-value">18.5K</div>
          <div className="adm-blog-stat-label">Total Views</div>
        </div>
        <div className="adm-blog-stat-card">
          <div className="adm-blog-stat-icon">💬</div>
          <div className="adm-blog-stat-value">342</div>
          <div className="adm-blog-stat-label">Comments</div>
        </div>
      </div>
      <button className="adm-blog-action-btn" onClick={() => navigate('/admin/blog')}>
        ✍️ Open Blog Admin Panel
      </button>
    </>
  );

  /* ════════════════════════════
     TAB: SETTINGS
     ════════════════════════════ */
  const [settingsPassword, setSettingsPassword] = useState({ current: '', newPass: '', confirm: '' });
  const [settingsMsg, setSettingsMsg] = useState('');

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (settingsPassword.newPass !== settingsPassword.confirm) {
      setSettingsMsg('Passwords do not match.');
      return;
    }
    if (settingsPassword.current !== 'S4U@dmin2026!') {
      setSettingsMsg('Current password is incorrect.');
      return;
    }
    setSettingsMsg('Password updated successfully! (Demo only)');
    setSettingsPassword({ current: '', newPass: '', confirm: '' });
  };

  const renderSettings = () => (
    <div className="adm-settings-grid">
      {/* Admin profile */}
      <div className="adm-settings-card">
        <h3>Admin Profile</h3>
        <div className="adm-settings-field">
          <label>Admin ID</label>
          <input type="text" value="stocks4u_admin" readOnly />
        </div>
        <div className="adm-settings-field">
          <label>Role</label>
          <input type="text" value="Super Admin" readOnly />
        </div>
        <div className="adm-settings-field">
          <label>Email</label>
          <input type="text" value="admin@stocks4u.in" readOnly />
        </div>
      </div>

      {/* Change password */}
      <div className="adm-settings-card">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordChange}>
          <div className="adm-settings-field">
            <label>Current Password</label>
            <input
              type="password"
              value={settingsPassword.current}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, current: e.target.value })}
              placeholder="Enter current password"
            />
          </div>
          <div className="adm-settings-field">
            <label>New Password</label>
            <input
              type="password"
              value={settingsPassword.newPass}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, newPass: e.target.value })}
              placeholder="Enter new password"
            />
          </div>
          <div className="adm-settings-field">
            <label>Confirm Password</label>
            <input
              type="password"
              value={settingsPassword.confirm}
              onChange={(e) => setSettingsPassword({ ...settingsPassword, confirm: e.target.value })}
              placeholder="Confirm new password"
            />
          </div>
          {settingsMsg && (
            <p style={{
              fontSize: '0.82rem',
              color: settingsMsg.includes('success') ? '#34d399' : '#f87171',
              marginBottom: '0.75rem',
            }}>{settingsMsg}</p>
          )}
          <button type="submit" className="adm-settings-save-btn">Update Password</button>
        </form>
      </div>

      {/* System health */}
      <div className="adm-settings-card" style={{ gridColumn: '1 / -1' }}>
        <h3>System Health</h3>
        <div className="adm-health-list">
          <div className="adm-health-item">
            <div className="adm-health-left">
              <span className="adm-health-dot green" />
              <span className="adm-health-name">API Server</span>
            </div>
            <span className="adm-health-status good">Operational</span>
          </div>
          <div className="adm-health-item">
            <div className="adm-health-left">
              <span className="adm-health-dot green" />
              <span className="adm-health-name">Database</span>
            </div>
            <span className="adm-health-status good">Operational</span>
          </div>
          <div className="adm-health-item">
            <div className="adm-health-left">
              <span className="adm-health-dot green" />
              <span className="adm-health-name">CDN</span>
            </div>
            <span className="adm-health-status good">Operational</span>
          </div>
          <div className="adm-health-item">
            <div className="adm-health-left">
              <span className="adm-health-dot yellow" />
              <span className="adm-health-name">WebSocket Service</span>
            </div>
            <span className="adm-health-status warning">Degraded</span>
          </div>
          <div className="adm-health-item">
            <div className="adm-health-left">
              <span className="adm-health-dot green" />
              <span className="adm-health-name">Payment Gateway</span>
            </div>
            <span className="adm-health-status good">Operational</span>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Tab title map ── */
  const tabTitles = {
    dashboard: { title: 'Dashboard Overview', sub: 'Monitor your platform KPIs and analytics' },
    users: { title: 'User Management', sub: 'View and manage all registered users' },
    subscriptions: { title: 'Subscriptions', sub: 'Plan analytics and revenue tracking' },
    analytics: { title: 'Analytics', sub: 'Deep dive into platform usage data' },
    blog: { title: 'Blog Manager', sub: 'Manage blog posts and content' },
    settings: { title: 'Settings', sub: 'Admin profile and system configuration' },
  };

  /* ── Tab content map ── */
  const tabRenderers = {
    dashboard: renderDashboard,
    users: renderUsers,
    subscriptions: renderSubscriptions,
    analytics: renderAnalytics,
    blog: renderBlogManager,
    settings: renderSettings,
  };

  /* ═══════════════════════════════
     RENDER
     ═══════════════════════════════ */
  return (
    <div className="admin-dashboard-wrapper">
      {/* Sidebar overlay (mobile) */}
      <div
        className={`adm-sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── SIDEBAR ── */}
      <aside className={`adm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="adm-sidebar-header">
          <div className="adm-logo-icon">📊</div>
          <div>
            <div className="adm-logo-text">Stocks4U</div>
            <span className="adm-logo-sub">Admin Portal</span>
          </div>
        </div>

        <nav className="adm-nav">
          <div className="adm-nav-section">
            <div className="adm-nav-label">Main</div>
            {navItems.slice(0, 4).map((item) => (
              <button
                key={item.id}
                className={`adm-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              >
                <span className="adm-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          <div className="adm-nav-section">
            <div className="adm-nav-label">Tools</div>
            {navItems.slice(4).map((item) => (
              <button
                key={item.id}
                className={`adm-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              >
                <span className="adm-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="adm-sidebar-footer">
          <div className="adm-user-card">
            <div className="adm-user-avatar">SA</div>
            <div>
              <div className="adm-user-name">Super Admin</div>
              <div className="adm-user-role">stocks4u_admin</div>
            </div>
          </div>
          <button className="adm-logout-btn" onClick={handleAdminLogout}>
            🚪 Log Out
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div className="adm-main">
        {/* Top bar */}
        <div className="adm-topbar">
          <button className="adm-mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <div className="adm-topbar-left">
            <h1>{tabTitles[activeTab]?.title}</h1>
            <p>{tabTitles[activeTab]?.sub}</p>
          </div>
          <div className="adm-topbar-actions">
            <button className="adm-topbar-btn" onClick={() => alert('Data exported!')}>
              <span className="adm-topbar-btn-icon">📥</span>
              <span>Export</span>
            </button>
            <button className="adm-topbar-btn" onClick={() => window.location.reload()}>
              <span className="adm-topbar-btn-icon">🔄</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="adm-content">
          {tabRenderers[activeTab]?.()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
